import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { pprwAPI } from '../../services/api';
import { EntityCustomFields } from '../shared/EntityCustomFields';
import { useAuth } from '../../context/AuthContext';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const DELIVERY_TYPES = ['Live', 'Simu Live'];
const SOLUTION_AREAS = ['AI Business Solutions', 'Cloud and AI Platforms', 'Security'];
const TIMEZONES = ['IST', 'BST', 'GMT', 'PST', 'PDT'];

export const taxItems = (cats, id) => (cats.find(c => c.id === id)?.items || []).filter(i => i.active !== false);

const Field = ({ label, children, span2 = false }) => (
  <div className={span2 ? 'col-span-2' : ''}>
    <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
    {children}
  </div>
);

// Renders a taxonomy-backed dropdown; falls back to a free-text input when the
// taxonomy category has no items yet (e.g. MS Owners not populated in Settings).
const TaxonomySelect = ({ items, value, onChange, placeholder, testId }) => {
  if (!items || items.length === 0) {
    return <Input value={value} onChange={e => onChange(e.target.value)} placeholder={`${placeholder} (add options in Settings → Taxonomy)`} data-testid={testId} />;
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{items.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export function WorkshopFormDialog({ open, onClose, onSaved, existing, categories = [] }) {
  const isEdit = !!existing;
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: existing?.title || '',
    solution_area: existing?.solution_area || '',
    ms_owner: existing?.ms_owner || '',
    pmo: existing?.pmo || '',
    delivery_platform: existing?.delivery_platform || '',
    delivery_type: existing?.delivery_type || 'Live',
    quarter: existing?.quarter || '',
    start_date: existing?.start_date || '',
    end_date: existing?.end_date || '',
    po_number: existing?.po_number || '',
    series_journey_title: existing?.series_journey_title || '',
    content_owner: existing?.content_owner || '',
    lab_owner: existing?.lab_owner || '',
    num_days: existing?.num_days != null ? String(existing.num_days) : '',
    duration_hours: existing?.duration_hours != null ? String(existing.duration_hours) : '',
    description: existing?.description || '',
    custom_fields: existing?.custom_fields || {},
  });
  const [customFieldKeys, setCustomFieldKeys] = useState(Object.keys(existing?.custom_fields || {}));
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addSlot = () => setSlots(s => [...s, { time_zone: '', delivery_type: form.delivery_type || 'Live', start_time: '', end_time: '', trainer_1: '' }]);
  const addPreset = (tzList) => setSlots(s => {
    const existing = new Set(s.map(r => r.time_zone).filter(Boolean));
    const add = tzList.filter(tz => !existing.has(tz)).map(tz => ({ time_zone: tz, delivery_type: form.delivery_type || 'Live', start_time: '', end_time: '', trainer_1: '' }));
    return [...s, ...add];
  });
  const setSlot = (i, k, v) => setSlots(s => s.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeSlot = (i) => setSlots(s => s.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Course / Workshop title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        num_days: form.num_days ? parseFloat(form.num_days) : null,
        duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : null,
      };
      if (isEdit) { await pprwAPI.workshops.update(existing.id, payload); toast.success('Workshop updated — common attributes synced to its sessions'); }
      else {
        const validSlots = slots.filter(s => s.time_zone);
        await pprwAPI.workshops.create({ ...payload, slots: validSlots });
        toast.success(validSlots.length ? `Workshop created with ${validSlots.length} session${validSlots.length !== 1 ? 's' : ''}` : 'Workshop created');
      }
      onSaved(); onClose();
    } catch (_e) { toast.error('Failed to save workshop'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workshop-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Workshop' : 'New Workshop'}</DialogTitle>
          <DialogDescription>Common attributes shared by all sessions in this workshop. Editing these syncs down to every session inside.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Course / Workshop Title *" span2>
            <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Agentic AI Fundamentals for Partners" data-testid="workshop-title-input" />
          </Field>
          <Field label="Solution Area">
            <Select value={form.solution_area} onValueChange={v => set('solution_area', v)}>
              <SelectTrigger data-testid="workshop-solution-area-select"><SelectValue placeholder="Select solution area" /></SelectTrigger>
              <SelectContent>{SOLUTION_AREAS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Delivery Type">
            <Select value={form.delivery_type} onValueChange={v => set('delivery_type', v)}>
              <SelectTrigger data-testid="workshop-delivery-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DELIVERY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="MS Owner">
            <TaxonomySelect items={taxItems(categories, 'ms_owner')} value={form.ms_owner} onChange={v => set('ms_owner', v)} placeholder="Select MS Owner" testId="workshop-ms-owner-select" />
          </Field>
          <Field label="PMO">
            <TaxonomySelect items={taxItems(categories, 'pmo')} value={form.pmo} onChange={v => set('pmo', v)} placeholder="Select PMO" testId="workshop-pmo-select" />
          </Field>
          <Field label="Delivery Platform">
            <TaxonomySelect items={taxItems(categories, 'delivery_platform')} value={form.delivery_platform} onChange={v => set('delivery_platform', v)} placeholder="Select platform" testId="workshop-delivery-platform-select" />
          </Field>
          <Field label="Quarter">
            <Select value={form.quarter} onValueChange={v => set('quarter', v)}>
              <SelectTrigger data-testid="workshop-quarter-select"><SelectValue placeholder="Select quarter" /></SelectTrigger>
              <SelectContent>{QUARTERS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Start Date">
            <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="workshop-start-date-input" />
          </Field>
          <Field label="End Date">
            <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="workshop-end-date-input" />
          </Field>
          <Field label="PO Number">
            <Input value={form.po_number} onChange={e => set('po_number', e.target.value)} placeholder="PO-2026-001" data-testid="workshop-po-input" />
          </Field>
          <Field label="Series / Journey Title">
            <Input value={form.series_journey_title} onChange={e => set('series_journey_title', e.target.value)} data-testid="workshop-series-input" />
          </Field>
          <Field label="Content Owner">
            <Input value={form.content_owner} onChange={e => set('content_owner', e.target.value)} data-testid="workshop-content-owner-input" />
          </Field>
          <Field label="Lab Owner">
            <Input value={form.lab_owner} onChange={e => set('lab_owner', e.target.value)} data-testid="workshop-lab-owner-input" />
          </Field>
          <Field label="No. of Days">
            <Input type="number" value={form.num_days} onChange={e => set('num_days', e.target.value)} data-testid="workshop-num-days-input" />
          </Field>
          <Field label="Duration (Hours)">
            <Input type="number" value={form.duration_hours} onChange={e => set('duration_hours', e.target.value)} data-testid="workshop-duration-input" />
          </Field>
          <Field label="Description" span2>
            <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional notes about this workshop" data-testid="workshop-description-input" />
          </Field>
          <div className="col-span-2 border-t border-border pt-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Additional taxonomy fields</p>
            <EntityCustomFields entityType="pprw_workshop"
              values={form.custom_fields || {}} onChange={(v) => set('custom_fields', v)}
              isSuperAdmin={user?.role === 'superadmin'} currentUser={user}
              testPrefix="workshop-custom-field" />
          </div>
          {!isEdit && (
            <div className="col-span-2 border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time-zone sessions (optional)</p>
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addSlot} data-testid="workshop-add-slot-btn">
                  <Plus className="w-3.5 h-3.5" />Add time zone
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="workshop-tz-presets">
                <span className="text-[11px] text-muted-foreground">Quick add:</span>
                {[
                  { label: 'IST + GMT + PST', tzs: ['IST', 'GMT', 'PST'] },
                  { label: 'IST + GMT', tzs: ['IST', 'GMT'] },
                  { label: 'GMT + PST', tzs: ['GMT', 'PST'] },
                ].map(p => (
                  <button key={p.label} type="button" onClick={() => addPreset(p.tzs)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-border hover:bg-lime/10 hover:border-lime/50 transition-colors"
                    data-testid={`workshop-tz-preset-${p.tzs.join('-')}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              {slots.length === 0 ? (
                <p className="text-xs text-muted-foreground">Add each time-zone occurrence (with its own Live / Simu Live type). Sessions are created inside this workshop and inherit its common attributes.</p>
              ) : (
                <div className="space-y-2">
                  {slots.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`workshop-slot-${i}`}>
                      <div className="col-span-3">
                        <Select value={s.time_zone} onValueChange={v => setSlot(i, 'time_zone', v)}>
                          <SelectTrigger className="h-9" data-testid={`workshop-slot-tz-${i}`}><SelectValue placeholder="Time zone" /></SelectTrigger>
                          <SelectContent>{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Select value={s.delivery_type} onValueChange={v => setSlot(i, 'delivery_type', v)}>
                          <SelectTrigger className="h-9" data-testid={`workshop-slot-type-${i}`}><SelectValue placeholder="Type" /></SelectTrigger>
                          <SelectContent>{DELIVERY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2"><Input className="h-9" type="time" value={s.start_time} onChange={e => setSlot(i, 'start_time', e.target.value)} data-testid={`workshop-slot-start-${i}`} /></div>
                      <div className="col-span-2"><Input className="h-9" type="time" value={s.end_time} onChange={e => setSlot(i, 'end_time', e.target.value)} data-testid={`workshop-slot-end-${i}`} /></div>
                      <div className="col-span-2"><Input className="h-9" value={s.trainer_1} onChange={e => setSlot(i, 'trainer_1', e.target.value)} placeholder="Trainer" data-testid={`workshop-slot-trainer-${i}`} /></div>
                      <button type="button" onClick={() => removeSlot(i)} className="col-span-1 p-1.5 rounded hover:bg-red-50 justify-self-center" data-testid={`workshop-slot-remove-${i}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="workshop-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (isEdit ? 'Save Changes' : 'Create Workshop')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WorkshopFormDialog;
